import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IpoLanguageService,
  IpoTranslatePipe,
  LoadingState,
} from '@ip-only/front-end-library';
import { CircleMarker, Polygon, Polyline } from 'leaflet';
import { Subject, takeUntil } from 'rxjs';
import { Language } from '../../../../shared/language-chooser/language-chooser-entities';
import { DeliveryReportPdfService } from '../../../../shared/pdf-tools/pdf-report/delivery-report-pdf.service';
import { RP_INSTALLATION_REPORT_OPENED } from '../../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../../shared/posthug-analytics/analytics.service';
import { RolloutProgressReportEntities } from '../../../../shared/self-inspection-reports/service/entities';
import { ToastsService } from '../../../../shared/toast/toast.service';
import { isTopLevelDomainFI } from '../../../../shared/util/deploy-helper';
import { ScreenSizeService } from '../../../../shared/util/screen-size/screen-size.service';
import { SPAN_ROLES_EDIT } from '../../../../user-roles-def/user-roles';
import { UserService } from '../../../../user/user.service';
import { DuctService } from '../../../service/map/duct/duct.service';
import {
  DistributionPointProperties,
  DuctProperties,
  PointType,
} from '../../../span-progress-entities';
import { ReportModalService } from '../../../span-progress-report/report-modal.service';
import {
  createPopupPdfContext,
  PopupPdfDownloadController,
} from '../popup-pdf-download.controller';
import {
  buildRolloutReportUrl,
  cancelPdfDownloadOnPointChange,
  getIfsFromUrl,
  POPUP_PDF_DOWNLOAD_TOOLTIP,
  sourceKeyOrIdIdentityChanged,
} from '../popup-pdf.utils';

@Component({
  selector: 'app-households-point-popup-content',
  templateUrl: 'households-point-popup-content.component.html',
  styleUrls: ['households-point-popup-content.component.scss'],
  standalone: false,
})
export class HouseholdsPopupContentComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() properties: DistributionPointProperties;
  @Input() clickedObject: CircleMarker | Polyline | Polygon;
  @Output() dismiss = new EventEmitter();

  spanRolesEdit = SPAN_ROLES_EDIT;
  userRoles = this.userService.getPortalUserOnce()?.roles;
  pointTypeHP = PointType.Hp;
  pointTypeHC = PointType.Hc;

  isDeliveryPortalFI = isTopLevelDomainFI();

  subductProperties: DuctProperties | null = null;
  multiDuctProperties: DuctProperties | null = null;

  isExpandedDuctInformation = false;
  isMobileScreen = false;
  downloadTooltipLabel = POPUP_PDF_DOWNLOAD_TOOLTIP;
  language: Language;
  pdfController: PopupPdfDownloadController;

  loadingState: LoadingState;
  private cancelSub$ = new Subject<void>();
  private destroyed = false;

  constructor(
    private ductService: DuctService,
    private reportModalService: ReportModalService,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private changeDetector: ChangeDetectorRef,
    private screenSizeService: ScreenSizeService,
    private readonly languageService: IpoLanguageService,
    private readonly translate: IpoTranslatePipe,
    private readonly toastsService: ToastsService,
    private readonly pdfService: DeliveryReportPdfService,
    private readonly router: Router,
  ) {
    this.pdfController = new PopupPdfDownloadController(
      pdfService,
      toastsService,
      translate,
      createPopupPdfContext(
        () => {
          const ifsNumber = getIfsFromUrl(this.router.url);
          const deliveryOrderId =
            this.properties.leadId ?? this.properties.sourceKey;
          if (!ifsNumber || !deliveryOrderId) return null;
          return {
            ifsNumber,
            deliveryOrderId,
            locale: this.language ?? 'en',
            address: this.properties.name ?? '-',
            rolloutPointType: this.isHpReport
              ? RolloutProgressReportEntities.v3.MarkerTypes.HP
              : undefined,
          };
        },
        () => this.destroyed,
        () => this.changeDetector.markForCheck(),
      ),
    );
  }

  ngOnInit(): void {
    this.setIsMobileScreen();
    this.languageService
      .getLanguage$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((lang) => (this.language = lang as Language));
    this.ductService
      .getEntityLoadingState$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((loadingState) => {
        this.loadingState = loadingState;
        this.changeDetector.detectChanges();
      });
  }

  ngOnChanges(changes?: SimpleChanges): void {
    cancelPdfDownloadOnPointChange(changes, sourceKeyOrIdIdentityChanged, () =>
      this.pdfController.cleanup(),
    );
    this.getDuctInfo();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.pdfController.cleanup();
    this.cancelSub$.next();
    this.cancelSub$.complete();
  }

  getDuctInfo(): void {
    this.subductProperties = this.properties.subDuct ?? null;
    this.multiDuctProperties = this.properties.duct ?? null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setIsMobileScreen();
  }

  setIsMobileScreen() {
    this.isMobileScreen = this.screenSizeService.checkIsMobileScreen();
    this.isExpandedDuctInformation = this.isMobileScreen;
  }

  handleOnClickButton(): void {
    if (this.isHpReport) {
      this.openV3HpReportPage();
      return;
    }
    this.openInstallationReportModal();
  }

  toggleExpandableContent() {
    this.isExpandedDuctInformation = !this.isExpandedDuctInformation;
  }

  openInstallationReportModal() {
    this.reportModalService.setModalState({
      ['installationReportModalV2']: {
        open: true,
        data: this.properties,
      },
    });
    this.analyticsService.logEvent(RP_INSTALLATION_REPORT_OPENED);
  }

  openV3HpReportPage = (): void => {
    window.open(
      buildRolloutReportUrl(
        this.router.url,
        RolloutProgressReportEntities.v3.MarkerTypes.HP,
        this.properties.leadId ?? this.properties.sourceKey,
      ),
      '_blank',
    );
  };

  onDownloadPreviewPdf(event: MouseEvent): void {
    this.pdfController.onDownloadPreviewPdf(event);
  }

  onDownloadPreviewPdfKeyDown(event: KeyboardEvent): void {
    this.pdfController.onDownloadPreviewPdfKeyDown(event);
  }

  onClosePopup() {
    this.dismiss.emit();
  }

  get isHpReport(): boolean {
    return this.properties.markerTypeId === this.pointTypeHP;
  }

  get isHcReport(): boolean {
    return this.properties.markerTypeId === this.pointTypeHC;
  }

  get shouldShowPdfDownload(): boolean {
    return this.isHpReport || this.isHcReport;
  }
}
